import { bestE1rmSet, type E1rmCandidateSet } from "./e1rmPoints.js";
import { EFFORT_HARD_SET_RIR, effectiveRir } from "./effortScale.js";
import { WEEK_MS } from "./freshness.js";
import { E1RM_REP_CAP } from "./pr.js";
import type { LastLoggedSet } from "./resolveDefaultWeight.js";
import type { Exercise } from "./schemas/exercise.js";
import type { ProposalSource } from "./setProposal.js";
import { rankSwapCandidates } from "./similarity.js";

// P0 57d74690: the anchor of a «today» row (D1, D2, D9) and how often the
// exercise is done (D8). Pure: the caller fetches the sets.

/** The effort every draft set asks for: the hard-set line, so each set counts
 *  toward the gap it closes. */
export const TODAY_TARGET_RIR = EFFORT_HARD_SET_RIR;

/** The best e1RM set is read over this many weeks (D1). */
export const TODAY_ANCHOR_WEEKS = 8;

/** Most reps a weighted row asks for: the target's reps plus RIR 3 must stay
 *  inside Epley's band (D9 reads the anchor the same way), or the load chain
 *  declines and the kg cell stays empty. */
export const TODAY_MAX_REPS = E1RM_REP_CAP - TODAY_TARGET_RIR;

/** One logged set as the anchor reads it. `session` groups the sets. */
export type TodayAnchorSet = E1rmCandidateSet & {
  extra?: number;
  atMs: number;
  session: string;
};

export type TodayAnchor = {
  /** The reps the row proposes. */
  reps: number;
  /** What the load chain reads: the anchor set (kg and its own reps), or the
   *  extra of a bodyweight set. Null: nothing to compute a load from (a
   *  similar exercise, D2, or pure bodyweight). */
  chainSet: LastLoggedSet | null;
  /** When the anchor set was performed. */
  atMs: number;
  source: ProposalSource;
};

/** One exercise's history for the draft. `practice`: sessions in the last
 *  TODAY_ANCHOR_WEEKS weeks. */
export type TodayHistory = { anchor: TodayAnchor | null; practice: number };

type Picked = Omit<TodayAnchor, "source">;

// D9: the winner at its logged effort, the e1RM the load chain reads back.
const bestE1rmOf = (sets: readonly TodayAnchorSet[]): Picked | null => {
  const best = bestE1rmSet(sets, { atEffort: true });
  if (best === null) return null;
  return {
    reps: Math.min(best.reps, TODAY_MAX_REPS),
    chainSet: { weightKg: best.weightKg, reps: best.reps, rir: best.rir },
    atMs: best.set.atMs,
  };
};

// No e1RM in the session (bodyweight, or past 12 reps): the set with the most
// reps, first wins a tie. Its reps stay as they are: no load is computed.
const mostRepsOf = (sets: readonly TodayAnchorSet[]): Picked | null => {
  let best: TodayAnchorSet | null = null;
  for (const s of sets) {
    if (s.type !== "weighted" && s.type !== "bodyweight") continue;
    if ((s.reps ?? 0) <= (best?.reps ?? 0)) continue;
    best = s;
  }
  if (best === null || best.reps === undefined) return null;
  // A bodyweight extra is signed: an assisted set keeps its minus.
  const load = best.type === "weighted" ? best.weight : best.extra;
  return {
    reps: best.reps,
    chainSet:
      load !== undefined && load !== 0 && (best.type !== "weighted" || load > 0)
        ? { weightKg: load, reps: best.reps, rir: effectiveRir(best) }
        : null,
    atMs: best.atMs,
  };
};

const lastSessionOf = (sets: readonly TodayAnchorSet[]): TodayAnchorSet[] => {
  let last: TodayAnchorSet | null = null;
  for (const s of sets) if (last === null || s.atMs > last.atMs) last = s;
  return last === null ? [] : sets.filter((s) => s.session === last.session);
};

/** D1 and D8 for one exercise, from its sets in any order. */
export const todayHistoryFromSets = (
  slug: string,
  sets: readonly TodayAnchorSet[],
  nowMs: number,
): TodayHistory => {
  const from = nowMs - TODAY_ANCHOR_WEEKS * WEEK_MS;
  const recent = sets.filter((s) => s.atMs >= from);
  const practice = new Set(recent.map((s) => s.session)).size;
  const last = lastSessionOf(sets);
  const picked = bestE1rmOf(recent) ?? bestE1rmOf(last) ?? mostRepsOf(last);
  if (picked === null) return { anchor: null, practice };
  const sessionCount = new Set(sets.map((s) => s.session)).size;
  return { anchor: { ...picked, source: { kind: "exercise", slug, sessionCount } }, practice };
};

/** D2: the reps of the nearest similar exercise with an anchor of its own,
 *  in the swap ranking's order, never its kg. Null: none in `own`. */
export const similarTodayAnchor = (
  ex: Exercise,
  catalog: readonly Exercise[],
  own: ReadonlyMap<string, TodayAnchor | null>,
): TodayAnchor | null => {
  for (const c of rankSwapCandidates(ex, catalog)) {
    const a = own.get(c.exercise.slug);
    if (!a || a.source.kind !== "exercise") continue;
    return { reps: a.reps, chainSet: null, atMs: a.atMs, source: { ...a.source, kind: "similar" } };
  }
  return null;
};
